import {html} from 'lit-html';
import {repeat} from 'lit-html/directives/repeat';
import {classMap} from 'lit-html/directives/class-map';

import TemplateRegistry from '../template/template-registry';
import SavedConfigs from '../config/saved-configs';

const t = SavedConfigs.t;

const localDate = (value) => {
    const date = new Date(value);
    return value && !isNaN(date.getTime()) ? date.toLocaleDateString() : '';
};

// no name: the product handle and the variant label a saved configuration
const label = (entry) => [entry.productHandle, entry.variantId].filter(Boolean).join(', ');

// a preview that is gone ( an ordered preview goes with the bucket lifecycle ) shows the image of the variant or the product instead, once
const previewError = (configs, entry) => (event) => {
    const img = event.target;
    if (img.dataset.fallback) {
        img.style.display = 'none';
        return;
    }
    img.dataset.fallback = '1';
    const product = configs.po.settings.product || {};
    const variant = (product.variants || []).find((candidate) => String(candidate.id) === String(entry.variantId));
    const src = (variant && variant.featured_image && variant.featured_image.src) || product.featured_image;
    if (src) {
        img.src = src;
    } else {
        img.style.display = 'none';
    }
};

// one tile of either list: preview, title, meta, Open and optional Remove
const item = (configs, entry, title, meta, removable) => html`
    <div class="cl-po--library-grid-item cl-po--configs-item">
        <div class="cl-po--swatch" title="${t('open')}" @click=${() => configs.pick(entry)}>
            ${entry.previewUrl ? html`<img src="${entry.previewUrl}" alt="${title}" loading="lazy" @error=${previewError(configs, entry)}/>` : null}
        </div>
        <div class="cl-po--configs-name" title="${title}">${title}</div>
        <div class="cl-po--configs-meta">${meta}</div>
        <div class="cl-po--configs-actions">
            <button type="button" @click=${() => configs.pick(entry)}>${t('open')}</button>
            ${removable ? html`<button type="button" @click=${() => configs.confirmRemove(entry)}>${t('remove')}</button>` : null}
        </div>
    </div>
`;

const status = (text) => html`<div class="cl-po--configs-status">${text}</div>`;

const saved = (configs) => !configs.configs.length ? null : html`
    <div class="cl-po--library-grid cl-po--configs-grid">
        ${repeat(configs.configs, (entry) => entry.id, (entry) => item(configs, entry, label(entry), localDate(entry.savedDate), true))}
    </div>
`;

const orders = (configs) => {
    if (configs.ordersState === 'loading') return status('…');
    if (configs.ordersState === 'failed') return status(t('orders.error', null, 'Your orders could not be loaded'));
    if (!configs.orders.length) return status(t('orders.empty'));

    return html`
        <div class="cl-po--library-grid cl-po--configs-grid cl-po--configs-orders">
            ${repeat(configs.orders, (entry) => entry.lineId, (entry) =>
                item(configs, entry, entry.title, t('orders.order', { name: entry.orderName, date: localDate(entry.orderDate) }), false))}
        </div>
    `;
};

/**
 * Saved Configurations - the shopper's saved configurations and ordered lines of this configuration (no value of its own)
 * UI only: requests, open and the dialogs live in config/saved-configs.js (config.configs), the save button is the save-config option
 * Account login only - with params.pastOrders tabs "Saved" and "Past orders"
 * The ordered lines load when the shopper opens their tab, open only
 * params: pastOrders
 */
TemplateRegistry.templates['saved-configs'] = (option, config) => {
    const configs = config.configs;

    // no account login or hidden for the visit > the option hides itself, label included
    if (!configs || !configs.listShown()) return html`<style>[data-option="${option.name}"] { display:none; }</style>`;

    configs.load();

    const pastOrders = !!option.params.pastOrders;
    const ordersTab = pastOrders && configs.tab === 'orders';
    const tab = (name, text) => html`
        <button type="button" role="tab" aria-selected="${configs.tab === name ? 'true' : 'false'}"
                class="cl-po--configs-tab ${classMap({ 'cl-checked': configs.tab === name })}" @click=${() => configs.selectTab(name)}>${text}</button>
    `;

    return html`
        <div class="cl-po--configs">
            ${pastOrders ? html`
                <div class="cl-po--configs-tabs" role="tablist">
                    ${tab('configs', t('list.title'))}
                    ${tab('orders', t('orders.tab'))}
                </div>
            ` : configs.configs.length ? html`
                <div class="cl-po--configs-title">${t('list.title')}</div>
            ` : null}

            ${ordersTab ? orders(configs) : saved(configs)}
        </div>
    `;
};
